import { defineStore } from 'pinia'
import { StorageSerializers, useLocalStorage } from '@vueuse/core'
import type {
  AuthenticatedUserResponse,
  ConceptFilters,
  ConceptFilterState,
  ConceptPayload,
  SessionResponse,
  UserData,
  UserMeta
} from '~/models'

interface OriginalConcept {
  uuid: string
  payload: ConceptPayload
}

export const createConceptFilter = (
  filterBy: Partial<ConceptFilters> = {}
): ConceptFilterState => ({
  page: 1,
  phrase: '',
  sortBy: { key: '', direction: 'asc' },
  filterBy: {
    is_published: '',
    has_questions: '',
    has_content: '',
    ...filterBy
  },
  filterByTracks: [],
  filterByCourses: [],
  filterByQuartiles: [],
  filterByStatus: [],
  deleted: false
})

export const useUserStore = defineStore('user', () => {
  const isAuthenticated = useLocalStorage('lbt-is-authenticated', false)
  const userData = useLocalStorage<Partial<UserData>>('lbt-user-data', {})
  const userMeta = useLocalStorage<UserMeta>('lbt-user-meta', {
    total_concepts: 0,
    total_explored_concepts: 0,
    total_explored_concepts_expected: 0,
    total_explored_concepts_this_month: 0
  })
  const userRoles = useLocalStorage<string[]>('lbt-user-roles', [])
  const userSession = useLocalStorage<Partial<SessionResponse>>(
    'lbt-user-session',
    {}
  )
  // JSON serializer keeps tokens stored by earlier versions readable.
  const accessToken = useLocalStorage<string>('lbt-access-token', '', {
    serializer: StorageSerializers.object
  })

  // v1 stored tag names/codes; tag filters are UUIDs since v2.
  localStorage.removeItem('lbt-filter')
  localStorage.removeItem('lbt-filter-concept')

  const filter = useLocalStorage<ConceptFilterState>(
    'lbt-filter-v2',
    createConceptFilter()
  )

  const filterConcept = useLocalStorage<ConceptFilterState>(
    'lbt-filter-concept-v2',
    createConceptFilter({ is_published: 'true' })
  )

  const originalConcept = useLocalStorage<OriginalConcept | null>(
    'lbt-original-concept',
    null,
    { serializer: StorageSerializers.object }
  )
  const originalQuestions = useLocalStorage('lbt-original-questions', {})
  const undoDelete = ref(false)
  const triggerRefresh = ref(false)
  const undoSave = ref(false)

  // Drops selected tags that no longer exist, e.g. after tags were merged.
  function pruneUnknownTags(
    target: Pick<
      ConceptFilterState,
      'filterByTracks' | 'filterByCourses' | 'filterByQuartiles'
    >,
    knownUuids: string[]
  ) {
    const keys = [
      'filterByTracks',
      'filterByCourses',
      'filterByQuartiles'
    ] as const
    keys.forEach((key) => {
      const known = target[key].filter((uuid) => knownUuids.includes(uuid))
      if (known.length !== target[key].length) {
        target[key] = known
      }
    })
  }

  const setUser = (response: AuthenticatedUserResponse) => {
    isAuthenticated.value = true
    userData.value = response.data
    userMeta.value = response.meta
    userRoles.value = response.data.roles.map((role) => role.name)
  }

  const setAccessToken = (token: string) => {
    accessToken.value = token
  }

  const setUserSession = (session: SessionResponse) => {
    userSession.value = session
  }

  const clearUser = () => {
    isAuthenticated.value = false
    userData.value = {}
    userMeta.value = {
      total_concepts: 0,
      total_explored_concepts: 0,
      total_explored_concepts_expected: 0,
      total_explored_concepts_this_month: 0
    }
    userRoles.value = []
    userSession.value = {}
    accessToken.value = ''
    filter.value = createConceptFilter()
  }

  return {
    isAuthenticated,
    accessToken,
    userRoles,
    userSession,
    userData,
    userMeta,
    setUser,
    clearUser,
    setAccessToken,
    setUserSession,
    filter,
    filterConcept,
    pruneUnknownTags,
    originalConcept,
    originalQuestions,
    undoDelete,
    triggerRefresh,
    undoSave
  }
})
